import React from "react";

import { AccordionModel, AccordionPanel } from "@courses-pm2/api";

import { easings } from "@courses-pm2/accordion-component/src/ts/config/easings";

type AccordionPanelComponentProps = {
    /**
     * A reference to the primary model describing the entire accordion, so we
     * can use the helper methods to perform required rendering.
     */
    model: AccordionModel;

    /**
     * The model describing the content to render specifically within this
     * panel.
     */
    panel: AccordionPanel;

    /**
     * Specifies the index of the panel within the entire list of panels in the
     * accordion.
     */
    index: number;

    /**
     * Specifies if the panel should currently be expanded.
     */
    expanded: boolean;

    /**
     * Callback that's going to be toggled when the user clicks the header to
     * expand / collapse the panel.
     */
    onToggle(index: number): void;
};

/**
 * Renders a single panel within the accordion, handling the transition when the
 * component is being expanded / collapsed internally.
 */
export class AccordionPanelComponent extends React.PureComponent<AccordionPanelComponentProps> {
    /**
     * Will contain a reference to the element that's wrapping the content of
     * the panel, so we can run transitions on it within componentDidUpdate.
     */
    protected readonly contentRef: React.RefObject<HTMLDivElement | null> = React.createRef();

    /**
     * Specifies if the component was expanded prior to the current update, when
     * determining if we should run an expand / collapse transition within the
     * componentDidUpdate lifecycle hook.
     */
    protected wasExpanded: boolean = this.props.expanded;

    /**
     * Will contain a reference to the timeout that's used to reset the height
     * of the panel to auto once it's been fully expanded, so that we can
     * cancel this operation if user changes his mind mid-transition.
     */
    protected resetHeightTimeout: number;

    /**
     * If the component is expanded initally, then make sure that the container
     * is allowed to stretch!
     */
    public componentDidMount(): void {
        if (this.props.expanded && this.contentRef.current) {
            this.contentRef.current.style.height = "auto";
        }
    }

    /**
     * Run expand / collapse transition as a side effect of the expanded prop
     * being toggled on / off.
     */
    public componentDidUpdate(): void {
        const contentRef = this.contentRef.current;

        // If panel has been expanded or collapsed, then run a transition
        if (this.props.expanded !== this.wasExpanded && contentRef) {
            clearTimeout(this.resetHeightTimeout);

            // Start by locking the height of the content wrapper to the full
            // height of the content
            contentRef.style.height = `${contentRef.scrollHeight}px`;

            // Force a reflow before we're going to manage the transition
            contentRef.offsetHeight; // tslint:disable-line no-unused-expression

            if (!this.props.expanded) {
                // If we're collapsing, then run transition after back to 0px
                // height
                contentRef.style.height = "";
            } else {
                // ... Otherwise reset height to "auto" once the transition has
                // ended to allow responsively adjusting to size changes
                this.resetHeightTimeout = window.setTimeout(
                    () => { contentRef.style.height = "auto"; },
                    400,
                );
            }
        }

        // Remember if the panel is currently expanded
        this.wasExpanded = this.props.expanded;
    }

    /**
     * Primary render method.
     */
    public render(): React.JSX.Element {
        return (
            <div className="accordionPanel">
                {this.renderTitle()}
                {this.renderContent()}

                <style jsx>{`
                    .accordionPanel {
                        margin-bottom: 8px;
                    }
                `}</style>
            </div>
        );
    }

    /**
     * Helper that renders the title of the panel in a bar of it's own, which
     * will be allow the user to toggle the panel on / off on click.
     */
    // tslint:disable-next-line max-func-body-length
    protected renderTitle(): React.JSX.Element {
        const { model, panel, index, expanded } = this.props;
        const content = model.renderTitle(index);

        return (
            <div
                className={`accordionPanel__title ${expanded ? "accordionPanel__title--expanded" : ""}`}
                onClick={this.onTitleClick}
                role="button"
            >
                {panel.headingTag === "h1" && <h1>{content}</h1>}
                {panel.headingTag === "h2" && <h2>{content}</h2>}
                {panel.headingTag === "h3" && <h3>{content}</h3>}
                {panel.headingTag === "h4" && <h4>{content}</h4>}
                {panel.headingTag === "h5" && <h5>{content}</h5>}
                {panel.headingTag === "h6" && <h6>{content}</h6>}

                <i className="accordionPanel__chevron" />

                <style jsx>{`
                    .accordionPanel__title {
                        /** Allow position: relative within */
                        position: relative;

                        /** Set up sizing */
                        box-sizing: border-box;
                        min-height: 45px;
                        padding: 0 62px 0 15px;

                        /** Center content vertically */
                        display: flex;
                        align-items: center;

                        /** Apply box styling */
                        background: rgba(90, 60, 40, 0.08);
                        border-bottom: 2px solid rgba(90, 60, 40, 0.3);

                        /** Indicate that the element is clickable */
                        cursor: pointer;

                        /** Apply basic font styling */
                        line-height: 35px;

                        /** Fix title flickering post collapsing in Chrome */
                        transform: translateZ(0);

                        /** Prepare transition to when activating */
                        transition:
                            background 0.1s linear,
                            color 0.1s linear;

                        /** Apply active styling when expanded */
                        &.accordionPanel__title--expanded {
                            background: #dc4320;
                            color: #fff !important;
                        }

                        /** Apply hover styling */
                        &:hover {
                            color: #dc4320;
                        }
                    }

                    /**
                     * Rendering of the chevron at the right side of the title
                     */
                    .accordionPanel__chevron {
                        /** Position at the top-right of the bar */
                        position: absolute;
                        right: 20px;
                        top: 50%;
                        margin-top: -11px;

                        /** Set up sizing */
                        width: 22px;
                        height: 23px;

                        /** Render lines using pseudo elements */
                        &::before,
                        &::after {
                            /** Center the line vertically */
                            position: absolute;
                            top: 50%;
                            margin-top: -1px;

                            /** Render the line itself */
                            width: 16px;
                            height: 3px;
                            background: #383838;

                            /** Prepare transition */
                            transition:
                                background-color 0.1s linear,
                                transform 0.2s ${ easings.inOutQuad},
                                transform-origin 0.2s ${ easings.inOutQuad};

                            /** Force rendering of pseudo element */
                            content: "";
                        }

                        &::before {
                            /** Render line towards the left side */
                            right: 50%;

                            /** Rotate the line 45deg */
                            transform: translateY(5px) rotate(45deg);
                            transform-origin: right bottom;
                        }

                        &::after {
                            /** Render line towards the right side */
                            left: 50%;

                            /** Rotate the line 45deg */
                            transform: translateY(5px) rotate(-45deg);
                            transform-origin: left bottom;
                        }
                    }

                    .accordionPanel__title:hover .accordionPanel__chevron {
                        /** Adjust chevron color on hover */
                        &::before,
                        &::after {
                            background-color: #dc4320;
                        }
                    }

                    .accordionPanel__title--expanded .accordionPanel__chevron {
                        /** Adjust chevron color */
                        &::before,
                        &::after {
                            background-color: #fff !important;
                        }

                        /** Invert chevron direction */
                        &::before {
                            transform: translateY(-5px) rotate(-45deg);
                            transform-origin: right top;
                        }

                        &::after {
                            transform: translateY(-5px) rotate(45deg);
                            transform-origin: left top;
                        }
                    }

                    /**
                     * Adjust sizing of different kinds of heading tags
                     */
                    h1,
                    h2,
                    h3,
                    h4,
                    h5,
                    h6 {
                        /** Reset default margins */
                        margin: 0;
                        padding: calc(10px - 0.1em) 0;

                        /** Reset default text styling */
                        font-weight: normal;
                        line-height: 1.2em;
                    }

                    h1 {
                        font-size: 2em;
                    }

                    h2 {
                        font-size: 1.8em;
                    }

                    h3 {
                        font-size: 1.6em;
                    }

                    h4 {
                        font-size: 1.4em;
                    }

                    h5 {
                        font-size: 1.2em;
                    }

                    h6 {
                        font-size: 1em;
                    }
                `}</style>
            </div>
        );
    }

    /**
     * Helper that renders the content of the panel within a wrapper, which we
     * can expand and collapse based on state.
     */
    protected renderContent(): React.JSX.Element {
        const { model, index, expanded } = this.props;

        return (
            <div
                ref={this.contentRef}
                className={`accordionPanel__content ${expanded ? "accordionPanel__content--expanded" : ""}`}
            >
                <div className="accordionPanel__wrapper">
                    {model.renderPanel(index)}
                </div>

                <style jsx>{`
                    .accordionPanel__content {
                        /** Apply box styling */
                        background: rgba(90, 60, 40, 0.08);

                        /** Prepare expand transition */
                        overflow: hidden;
                        height: 0;
                        transition: height 0.4s ${ easings.inOutQuart};
                    }

                    .accordionPanel__wrapper {
                        /** Apply internal padding */
                        padding: 30px;

                        /** Prepare fade in of content */
                        opacity: 0;
                        transition: opacity 0.4s linear;

                        /** Reset margins of nested custom elements */
                        & :global(> :first-child) {
                            margin-top: 0;
                        }

                        & :global(> :last-child) {
                            margin-bottom: 0;
                        }
                    }

                    .accordionPanel__content--expanded .accordionPanel__wrapper {
                        opacity: 1;
                    }
                `}</style>
            </div>
        );
    }

    /**
     * Listener that's triggered when the titlebar is being clicked, propagating
     * the event up to the parent component.
     */
    protected onTitleClick = () => {
        this.props.onToggle(this.props.index);
    }
}
